/**
 * De enige plek in de frontend die het netwerk raakt.
 *
 * De app weet niets van Zermelo en heeft geen token: hij bevraagt de eigen Worker
 * en krijgt daar een al genormaliseerd rooster terug. Dat is de kern van de
 * auth-proxy uit docs/architectuur.md, en de reden dat dit bestand zo kort is.
 */
import { config } from './config';
import { ApiFout, type ApiStatus, type Rooster } from './types';

/** Plakt het pad achter de geconfigureerde basis-URL. */
function url(pad: string): string {
  return `${config.apiBase}${pad}`;
}

/**
 * Haalt en controleert een JSON-antwoord.
 *
 * De backend levert bij een fout `{ fout, melding }`, waarbij `melding` al
 * Nederlands en voor een leerling leesbaar is ("Dit leerlingnummer staat niet in
 * het rooster."). Die geven we dus door in plaats van er zelf een tekst bij te
 * verzinnen — één plek waar de foutteksten staan, en dat is de backend.
 */
async function haalJson<T>(pad: string, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url(pad), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: signal ?? null,
    });
  } catch (err) {
    // Een afgebroken request is geen storing: dat is de app zelf die opruimt.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiFout('Geen verbinding met de server.');
  }

  const body: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const fout = body as { fout?: string; melding?: string } | null;
    throw new ApiFout(
      res.status === 404 || fout?.fout === 'ONBEKEND_NUMMER'
        ? 'Leerlingnummer niet herkend'
        : fout?.melding ?? `Serverfout (${res.status})`,
      fout?.fout ?? null,
      res.status,
    );
  }
  if (body === null) throw new ApiFout('Onleesbaar antwoord van de server.');

  return body as T;
}

/**
 * Het dagrooster van één leerling. Dit is het enige endpoint dat de tablet nodig heeft.
 *
 * @param dag 'YYYY-MM-DD', of null voor vandaag volgens de klok van de server. De
 *        tablet vult hem altijd in — ook voor vandaag — zodat de dag die op het
 *        scherm staat en de dag die is opgevraagd niet uiteen kunnen lopen als de
 *        tablet net over middernacht heen tikt.
 */
export function haalRooster(
  leerlingnummer: string,
  dag: string | null = null,
  signal?: AbortSignal,
): Promise<Rooster> {
  const query = dag ? `?dag=${encodeURIComponent(dag)}` : '';
  return haalJson<Rooster>(`/api/rooster/${encodeURIComponent(leerlingnummer)}${query}`, signal);
}

/**
 * De status van de koppeling.
 *
 * Optioneel: het inlogscherm gebruikt dit alleen om de demonummers te tonen als de
 * backend op dummydata draait. Faalt het, dan werkt het inlogscherm gewoon door.
 */
export function haalStatus(signal?: AbortSignal): Promise<ApiStatus> {
  return haalJson<ApiStatus>('/api/status', signal);
}
